import {
  ChatOutlined,
  PeopleAltOutlined,
  RestaurantMenuOutlined,
  SettingsOutlined,
  ShoppingBagOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import AnalyticsOutlinedIcon from '@mui/icons-material/AnalyticsOutlined';
import BrushOutlinedIcon from '@mui/icons-material/BrushOutlined';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import i18n from 'app-i18n';
import RouteWrapper from 'components/route-wrapper/route-wrapper.component';
import SidebarBottomGroup from 'components/sidebar/sidebar-bottom-group/sidebar-bottom-group.component';
import SidebarBottomLink from 'components/sidebar/sidebar-bottom-link/sidebar-bottom-link.component';
import SidebarGroup from 'components/sidebar/sidebar-group/sidebar-group.component';
import SidebarLink from 'components/sidebar/sidebar-link/sidebar-link.component';
import SidebarSection from 'components/sidebar/sidebar-section/sidebar-section.component';
import { lazy, ReactElement } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';
import { AccessDefinition } from 'util/access';
import { FeatureFlag } from 'util/client-configuration';
import NotFound from './not-found/not-found.component';
import EventEmails from './communications/email-templates/tabs/event-emails/event-emails.component';
import LoyaltyRewardEmails from './communications/email-templates/tabs/loyalty-reward-emails/loyalty-reward-emails.component';
import MessageEmails from './communications/email-templates/tabs/message-emails/message-emails.component';
import OfferEmails from './communications/email-templates/tabs/offer-emails/offer-emails.component';
import Overview from './analytics/email-pulse/overview/overview.component';
import EmailPulseMessages from './analytics/email-pulse/messages/messages.component';
import EmailPulseOffers from './analytics/email-pulse/offers/offers.component';
import EmailPulseOrders from './analytics/email-pulse/orders/orders.component';
import EmailPulseGiftCards from './analytics/email-pulse/gift-cards/gift-cards.component';
import EmailPulseSecurity from './analytics/email-pulse/security/security.component';
import Marketing from './analytics/sms-reports/sections/marketing/marketing.component';
import OrderConfirmation from './analytics/sms-reports/sections/order-confirmation/order-confirmation.component';
import AccountVerification from './analytics/sms-reports/sections/account-verification/account-verification.component';
import GuestCheckoutVerification from './analytics/sms-reports/sections/guest-checkout-verification/guest-checkout-verification.component';
import PasswordlessAuthentication from './analytics/sms-reports/sections/passwordless-authentication/passwordless-authentication.component';
import BrandLoyaltyOverview from './loyalty-performance-report/brand-loyalty-overview/brand-loyalty-overview.component';
import LocationLoyaltyPerformance from './loyalty-performance-report/location-loyalty-performance/location-loyalty-performance.component';
import LocationLoyaltyTrends from './loyalty-performance-report/location-loyalty-trends/location-loyalty-trends.component';
import LoyaltyOfferPerformance from './loyalty-performance-report/loyalty-offer-performance/loyalty-offer-performance.component';
import LoyaltyOfferTrends from './loyalty-performance-report/loyalty-offer-trends/loyalty-offer-trends.component';
import FranchiseLoyaltyOverview from './loyalty-performance-report/franchise-loyalty-overview/franchise-loyalty-overview.component';
import NormalHours from './locations/sections/hours/tabs/normal-hours/normal-hours.component';
import TemporaryHours from './locations/sections/hours/tabs/temporary-hours/temporary-hours.component';
import OrderLimits from './locations/sections/kitchen-capacity/tabs/order-limits/order-limits.component';
import PreparationTime from './locations/sections/kitchen-capacity/tabs/preparation-time/preparation-time.component';
import GroupsImported from './menu-management/groups/groups-landing/groups-imported/groups-imported.component';
import GroupsNative from './menu-management/groups/groups-landing/groups-native/groups-native.component';
import ItemsImported from './menu-management/items/items-landing/items-imported/items-imported.component';
import ItemsNative from './menu-management/items/items-landing/items-native/items-native.component';
import MenusImported from './menu-management/menus/menus-landing/menus-imported/menus-imported.component';
import MenusNative from './menu-management/menus/menus-landing/menus-native/menus-native.component';
import ItemAnalysis from './menu-management/recommendations/item-analysis/item-analysis.component';
import CustomRules from './menu-management/recommendations/custom-rules/custom-rules.component';
import RecommendationsSettings from './menu-management/recommendations/recommendations-settings/recommendations-settings.component';
import ItemClassifier from './menu-management/recommendations/item-classifier/item-classifier.component';
import MoneyMovementAccountConfiguration from './gift-cards/money-movement/sections/money-movement-account-configuration/money-movement-account-configuration.component';
import MoneyMovementTransactions from './gift-cards/money-movement/sections/money-movement-transactions/money-movement-transactions.component';
// Eager-load these tabbed-dashboard / list landing shells. Their heavy tab and
// section children are already eager (imported above), so the lazy parent chunk
// was only a thin shell — but navigating to it still paid a chunk-load +
// Suspense gap that intermittently pushed post-navigation element lookups past
// the E2E command timeout (top nav flakes: sms-reports, email-pulse,
// loyalty-performance, native-menus). Eager-loading removes that gap at
// marginal bundle cost. Parents of still-lazy children stay lazy.
import EmailPulse from './analytics/email-pulse/email-pulse.component';
import SMSReportLanding from './analytics/sms-reports/sms-reports-landing/sms-reports-landing.component';
import LoyaltyPerformanceReport from './loyalty-performance-report/loyalty-performance-report.component';
import MenusLanding from './menu-management/menus/menus-landing/menus-landing.component';
import GuestJourney from './analytics/guest-journey/guest-journey.component';
import { GUEST_JOURNEY_TAB_GATES } from './analytics/guest-journey/guest-journey-tab-gates';
import GuestJourneyActiveCustomers from './analytics/guest-journey/active-customers/active-customers.component';
import GuestJourneySliders from './analytics/guest-journey/sliders/sliders.component';
import GuestJourneyChurned from './analytics/guest-journey/churned/churned.component';
import GuestJourneyStageMovement from './analytics/guest-journey/stage-movement/stage-movement.component';
import GuestJourneyOfferImpact from './analytics/guest-journey/offer-impact/offer-impact.component';
import GuestJourneyDetails from './analytics/guest-journey/details/details.component';
import GuestJourneyRetention from './analytics/guest-journey/retention/retention.component';
import GuestJourneyBenchmarks from './analytics/guest-journey/benchmarks/benchmarks.component';
import GuestJourneySettings from './analytics/guest-journey/settings/guest-journey-settings.component';

const DisputeChargebackForm = lazy(
  () =>
    import(
      'components/forms/dispute-chargeback-form/dispute-chargeback-form.component'
    ),
);
const GiftCardPoolReport = lazy(
  () =>
    import('./analytics/gift-card-pool-report/gift-card-pool-report.component'),
);
const MenuIntelligence = lazy(
  () => import('./analytics/menu-intelligence/menu-intelligence.component'),
);
const SalesSummary = lazy(
  () => import('./analytics/sales-summary/sales-summary.component'),
);
const OnlineOrders = lazy(
  () => import('./analytics/salesreport/online-orders.component'),
);
const Campaigns = lazy(
  () => import('./communications/campaigns/campaigns.component'),
);
const EmailTemplates = lazy(
  () => import('./communications/email-templates/email-templates.component'),
);
const CreateEditMessage = lazy(
  () =>
    import(
      './communications/messages/create-edit-message/create-edit-message.component'
    ),
);
const Messages = lazy(
  () => import('./communications/messages/messages.component'),
);
const CreateEditOffer = lazy(
  () =>
    import(
      './communications/offers/create-edit-offer/create-edit-offer.component'
    ),
);
const AppHomeScreen = lazy(
  () => import('./appearance/app-home-screen/app-home-screen.component'),
);
const Offers = lazy(() => import('./communications/offers/offers.component'));
const Surveys = lazy(
  () => import('./communications/surveys/surveys.component'),
);
const CreateEditSurvey = lazy(
  () =>
    import(
      './communications/surveys/create-edit-survey/create-edit-survey.component'
    ),
);
const PromoCodes = lazy(
  () => import('./communications/promocodes/promocodes.component'),
);
const CustomerDetails = lazy(
  () => import('./crm/customers/customer-details/customer-details.component'),
);
const CustomerTags = lazy(
  () => import('./crm/customers/customer-tags/customer-tags.component'),
);
const Customers = lazy(() => import('./crm/customers/customers-component'));
const CreateSegment = lazy(
  () => import('./crm/segment/create-segment.component'),
);
const Segments = lazy(() => import('./crm/segments/segments.component'));
const GiftCardDetails = lazy(
  () =>
    import(
      './gift-cards/gift-card-management/giftcard-details/giftcard-details.component'
    ),
);
const GiftCard = lazy(
  () => import('./gift-cards/gift-card-management/giftcard.component'),
);
const CreateLocation = lazy(
  () => import('./locations/create/create.component'),
);
const EditLocation = lazy(() => import('./locations/edit/edit.component'));
const Locations = lazy(() => import('./locations/locations.component'));
const EditHours = lazy(
  () => import('./locations/sections/hours/edit-hours.component'),
);
const EditInformation = lazy(
  () => import('./locations/sections/information/edit-information.component'),
);
const EditKitchenCapacity = lazy(
  () =>
    import(
      './locations/sections/kitchen-capacity/edit-kitchen-capacity.component'
    ),
);
const MenuConfiguration = lazy(
  () =>
    import(
      './locations/sections/menu-configuration/menu-configuration.component'
    ),
);
const OrderingConfiguration = lazy(
  () =>
    import(
      './locations/sections/ordering-configuration/ordering-configuration.component'
    ),
);
const DeliverySettings = lazy(
  () =>
    import(
      './locations/sections/ordering/delivery-settings/delivery-settings.component'
    ),
);
const PickupSettings = lazy(
  () =>
    import(
      './locations/sections/ordering/pickup-settings/pickup-settings.component'
    ),
);
const PaymentSettings = lazy(
  () =>
    import(
      './locations/sections/payment-settings/locations-payment-settings.component'
    ),
);
const CreateGroup = lazy(
  () => import('./menu-management/groups/create-group/create-group.component'),
);
const EditGroup = lazy(
  () => import('./menu-management/groups/edit-group/edit-group.component'),
);
const GroupsLanding = lazy(
  () =>
    import('./menu-management/groups/groups-landing/groups-landing.component'),
);
const ImageUploadDetails = lazy(
  () =>
    import(
      './menu-management/image-upload/image-upload-details/image-upload-details.component'
    ),
);
const ImageUpload = lazy(
  () => import('./menu-management/image-upload/image-upload.component'),
);
const CreateItem = lazy(
  () => import('./menu-management/items/create-item/create-item.component'),
);
const EditItem = lazy(
  () => import('./menu-management/items/edit-item/edit-item.component'),
);
const ItemsLanding = lazy(
  () => import('./menu-management/items/items-landing/items-landing.component'),
);
const MenuSync = lazy(
  () => import('./menu-management/menu-sync/menu-sync.component'),
);
const MenusCreate = lazy(
  () => import('./menu-management/menus/create-menu/create-menu.component'),
);
const MenuEdit = lazy(
  () => import('./menu-management/menus/edit-menu/edit-menu.component'),
);
const OrderDetails = lazy(
  () => import('./order/order-details/order-details.component'),
);
const Accounts = lazy(() => import('./payments/accounts/accounts.component'));
const Chargebacks = lazy(
  () => import('./payments/chargebacks/chargebacks.component'),
);
const DepositsSummary = lazy(
  () => import('./payments/deposits-summary/deposits-summary.component'),
);
const ReportsLanding = lazy(
  () => import('./payments/reports-landing/reports-landing.component'),
);
const Reports = lazy(() => import('./payments/reports/reports.component'));
const Transactions = lazy(
  () => import('./payments/transactions/transactions.component'),
);
const ClientConfiguration = lazy(
  () =>
    import('./settings/client-configuration/client-configuration.component'),
);
const Client = lazy(() => import('./settings/client/client.component'));
const Employees = lazy(
  () => import('./settings/employees/employees.component'),
);
const GiftCardProviderConfiguration = lazy(
  () =>
    import(
      './settings/integrations/gift-cards/provider-configuration/provider-configuration.component'
    ),
);
const Integrations = lazy(
  () => import('./settings/integrations/integrations.component'),
);
const LoyaltySpec = lazy(
  () => import('./settings/loyalty-spec/loyalty-spec.component'),
);
const Roles = lazy(() => import('./settings/roles/roles.component'));
const Settings = lazy(() => import('./settings/settings.component'));
const Welcome = lazy(() => import('./welcome/welcome.component'));
const General = lazy(() => import('./settings/general/general.component'));
const Recommendations = lazy(
  () => import('./menu-management/recommendations/recommendations.component'),
);
const MoneyMovementLanding = lazy(
  () =>
    import(
      './gift-cards/money-movement/money-movement-landing/money-movement-landing.component'
    ),
);
const ACHLocationDetails = lazy(
  () =>
    import(
      './gift-cards/money-movement/ach-location-details/ach-location-details.component'
    ),
);
const AddFundingAccount = lazy(
  () =>
    import(
      './gift-cards/money-movement/dwolla/add-funding-account/add-funding-account.component'
    ),
);
const EnrollACHLocation = lazy(
  () =>
    import(
      './gift-cards/money-movement/dwolla/enroll-ach-location/enroll-ach-location.component'
    ),
);
const LoyaltyOffers = lazy(
  () =>
    import('./communications/offers/loyalty-offers/loyalty-offers.component'),
);
const RecommendationsReport = lazy(
  () => import('./recommendations-report/recommendations-report.component'),
);
const AddDocuments = lazy(
  () =>
    import(
      './gift-cards/money-movement/dwolla/add-documents/add-documents.component'
    ),
);
const RetryVerification = lazy(
  () =>
    import(
      './gift-cards/money-movement/dwolla/retry-verification/retry-verification.component'
    ),
);
const AddBeneficialOwners = lazy(
  () =>
    import(
      './gift-cards/money-movement/dwolla/add-beneficial-owners/add-beneficial-owners.component'
    ),
);

export type SidebarRouteConfig = {
  title?: () => string;
  icon?: React.ReactNode;
  component: (props: unknown) => ReactElement;
};

export type RouteHandleType = {
  page?: {
    title?: () => string;
    breadcrumb?: boolean;
    sidebar?: SidebarRouteConfig;
  };
};

export type IncentivioRouteObject = {
  children?: IncentivioRouteObject[];
  access?: AccessDefinition;
  handle?: RouteHandleType;
} & Omit<RouteObject, 'children'>;

const locationRoutes: IncentivioRouteObject = {
  path: 'locations',
  access: {
    one: {
      permission: {
        name: 'VIEW_STORE_ORDERING',
      },
    },
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.locations.title'),
      sidebar: {
        component: SidebarLink,
        icon: <StorefrontOutlined />,
      },
    },
  },
  children: [
    {
      index: true,
      access: {
        one: {
          permission: {
            name: 'VIEW_STORE_ORDERING',
          },
        },
      },
      element: <RouteWrapper element={<Locations />} />,
    },
    {
      path: ':locationId',
      access: {
        one: {
          permission: {
            name: 'EDIT_STORE_ORDERING',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('page.locations.edit.title'),
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<EditLocation />} />,
        },
        {
          path: 'locationinfo',
          element: <RouteWrapper element={<EditInformation />} />,
          handle: {
            page: {
              title: () =>
                i18n.t(
                  'page.locations.edit.tabs.general.sections.locationInfo.title',
                ),
            },
          },
        },
        {
          path: 'menus',
          element: <RouteWrapper element={<MenuConfiguration />} />,
          handle: {
            page: {
              title: () =>
                i18n.t(
                  'page.locations.edit.tabs.menu.sections.menuImportSettings.title',
                ),
            },
          },
        },
        {
          path: 'ordering',
          element: <RouteWrapper element={<OrderingConfiguration />} />,
          handle: {
            page: {
              title: () =>
                i18n.t(
                  'page.locations.edit.tabs.ordering.sections.orderingConfiguration.title',
                ),
            },
          },
        },
        {
          path: 'pickup',
          element: <RouteWrapper element={<PickupSettings />} />,
          handle: {
            page: {
              title: () => i18n.t('pickupSettings.title'),
            },
          },
        },
        {
          path: 'delivery',
          element: <RouteWrapper element={<DeliverySettings />} />,
          handle: {
            page: {
              title: () => i18n.t('deliverySettings.title'),
            },
          },
        },
        {
          path: 'hours',
          element: <RouteWrapper element={<EditHours />} />,
          children: [
            {
              index: true,
              element: <Navigate to="normal" replace />,
            },
            {
              path: 'normal',
              element: <RouteWrapper element={<NormalHours />} />,
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.locations.edit.tabs.ordering.sections.hours.title',
                    ),
                },
              },
            },
            {
              path: 'temporary',
              element: <RouteWrapper element={<TemporaryHours />} />,
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.locations.edit.tabs.ordering.sections.hours.title',
                    ),
                },
              },
            },
          ],
        },
        {
          path: 'kitchen-capacity',
          element: <RouteWrapper element={<EditKitchenCapacity />} />,
          children: [
            {
              index: true,
              element: <Navigate to="preptime" replace />,
            },
            {
              path: 'preptime',
              element: <RouteWrapper element={<PreparationTime />} />,
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.locations.edit.tabs.ordering.sections.kitchenCapacity.title',
                    ),
                },
              },
            },
            {
              path: 'orderlimits',
              element: <RouteWrapper element={<OrderLimits />} />,
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.locations.edit.tabs.ordering.sections.kitchenCapacity.title',
                    ),
                },
              },
            },
          ],
        },
        {
          path: 'payment-settings',
          element: <RouteWrapper element={<PaymentSettings />} />,
          handle: {
            page: {
              title: () => i18n.t('page.locations.edit.paymentSettings.title'),
            },
          },
        },
      ],
    },
    {
      path: 'create',
      access: {
        one: {
          permission: {
            name: 'CREATE_STORE',
          },
        },
      },
      element: <RouteWrapper element={<CreateLocation />} />,
    },
  ],
};

/**
 * Appearance groups the brand-facing look and feel of the guest app. Today that is
 * the mobile home screen; more surfaces are expected to join it.
 */
const appearanceRoutes: IncentivioRouteObject = {
  path: 'appearance',
  /*
   * The gate sits on the section rather than on the page beneath it: pruneRoutes
   * drops a route whose access fails but keeps a parent whose children have all
   * gone, so gating the child alone would leave an Appearance heading in the
   * sidebar with nothing under it. Everything here is app customization, so the
   * section is the right thing to switch off.
   *
   * `any` and `one` are both required — the checks are combined with AND — so this
   * reads as: one of those permissions, and the gate.
   */
  access: {
    any: [
      { permission: { name: 'VIEW_APP_HOME_CONFIG' } },
      { permission: { name: 'EDIT_APP_HOME_CONFIG' } },
    ],
    one: { statsigFeatureGate: { name: 'app-customization' } },
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.appearance.title'),
      sidebar: {
        icon: <BrushOutlinedIcon fontSize="small" />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'apphomescreen',
      handle: {
        page: {
          title: () => i18n.t('sidebar.appearance.appHomeScreen.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<AppHomeScreen />} />,
        },
      ],
    },
  ],
};

const giftCardsRoutes: IncentivioRouteObject = {
  path: 'giftcards',
  access: {
    any: [
      {
        permission: {
          name: 'VIEW_GIFT_CARD',
        },
      },
      {
        permission: {
          name: 'ACH_PORTAL_ACCESS',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.giftCards.title'),
      sidebar: {
        icon: <CardGiftcardOutlinedIcon />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'giftcardmanagement',
      access: {
        one: {
          permission: {
            name: 'VIEW_GIFT_CARD',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.giftCards.giftCardManagement.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<GiftCard />} />,
        },
        {
          path: ':cardNumber',
          element: <RouteWrapper element={<GiftCardDetails />} />,
          handle: {
            page: {
              title: () => i18n.t('page.giftCards.giftCardDetails.title'),
            },
          },
        },
      ],
    },
    {
      path: 'moneymovement',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-money-movement',
          },
          permission: {
            name: 'ACH_PORTAL_ACCESS',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.giftCards.moneyMovement.title'),
          sidebar: {
            component: SidebarLink,
          },
          breadcrumb: false,
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to="tabs" replace />,
        },
        {
          path: 'tabs',
          element: <RouteWrapper element={<MoneyMovementLanding />} />,
          handle: {
            page: {
              title: () => i18n.t('page.giftCards.moneyMovement.title'),
            },
          },
          children: [
            {
              index: true,
              element: <Navigate to="accountconfiguration" replace />,
            },
            {
              path: 'accountconfiguration',
              element: (
                <RouteWrapper element={<MoneyMovementAccountConfiguration />} />
              ),
            },
            {
              path: 'transactions',
              element: <RouteWrapper element={<MoneyMovementTransactions />} />,
            },
          ],
        },
        {
          path: ':locationId',
          children: [
            {
              index: true,
              element: <RouteWrapper element={<ACHLocationDetails />} />,
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.giftCards.moneyMovement.locationInformation.title',
                    ),
                },
              },
            },
            {
              element: <RouteWrapper element={<EnrollACHLocation />} />,
              access: {
                one: {
                  permission: {
                    name: 'ACH_PORTAL_ENROLL_LOCATION',
                  },
                },
              },
              path: 'enroll',
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.giftCards.moneyMovement.enrollACHLocation.title',
                    ),
                },
              },
            },
            {
              path: 'addfundingaccount',
              children: [
                {
                  path: ':externalId',
                  index: true,
                  element: <RouteWrapper element={<AddFundingAccount />} />,
                },
              ],
              handle: {
                page: {
                  title: () =>
                    i18n.t('page.giftCards.moneyMovement.fundingAccount.title'),
                },
              },
            },
            {
              path: 'adddocuments',
              children: [
                {
                  path: ':externalId',
                  index: true,
                  element: <RouteWrapper element={<AddDocuments />} />,
                },
              ],
              handle: {
                page: {
                  title: () =>
                    i18n.t('page.giftCards.moneyMovement.documents.title'),
                },
              },
            },
            {
              path: 'retryverification',
              children: [
                {
                  path: ':externalId',
                  index: true,
                  element: <RouteWrapper element={<RetryVerification />} />,
                },
              ],
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.giftCards.moneyMovement.retryVerification.title',
                    ),
                },
              },
            },
            {
              path: 'addbeneficialowners',
              children: [
                {
                  path: ':externalId',
                  index: true,
                  element: <RouteWrapper element={<AddBeneficialOwners />} />,
                },
              ],
              handle: {
                page: {
                  title: () =>
                    i18n.t(
                      'page.giftCards.moneyMovement.beneficialOwners.title',
                    ),
                },
              },
            },
          ],
        },
      ],
    },
  ],
};

const onlineOrdersRoutes: IncentivioRouteObject = {
  path: 'online-orders',
  access: {
    one: {
      permission: {
        name: 'VIEW_DAILY_ORDERS_REPORT',
      },
    },
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.onlineOrders.title'),
      sidebar: {
        component: SidebarLink,
        icon: <ShoppingBagOutlined />,
      },
    },
  },
  children: [
    {
      index: true,
      access: {
        one: {
          permission: {
            name: 'VIEW_DAILY_ORDERS_REPORT',
          },
        },
      },
      element: <RouteWrapper element={<OnlineOrders />} />,
    },
  ],
};

const analyticsRoutes: IncentivioRouteObject = {
  path: 'analytics',
  access: {
    any: [
      {
        featureFlag: {
          name: FeatureFlag.MenuIntelligence,
        },
      },
      {
        permission: {
          name: 'VIEW_GIFT_CARD_POOL_REPORT',
        },
      },
      {
        permission: {
          name: 'VIEW_SALES_SUMMARY',
        },
      },
      {
        permission: {
          name: 'VIEW_EMAIL_INSIGHTS_REPORT',
        },
      },
      {
        permission: {
          name: 'VIEW_REPORTS',
        },
      },
      {
        permission: {
          name: 'VIEW_LOYALTY_PERFORMANCE_REPORT',
        },
      },
      {
        permission: {
          name: 'VIEW_RECOMMENDATIONS',
        },
      },
      {
        permission: {
          name: 'VIEW_GUEST_JOURNEY',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.analytics.title'),
      sidebar: {
        icon: <AnalyticsOutlinedIcon />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'intelligence',
      access: {
        one: {
          featureFlag: {
            name: FeatureFlag.MenuIntelligence,
          },
        },
      },
      element: <RouteWrapper element={<MenuIntelligence />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.intelligence.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'giftcardpoolreport',
      element: <RouteWrapper element={<GiftCardPoolReport />} />,
      access: {
        one: {
          permission: {
            name: 'VIEW_GIFT_CARD_POOL_REPORT',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.giftcardpoolreport.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'salessummary',
      element: <RouteWrapper element={<SalesSummary />} />,
      access: {
        one: {
          permission: {
            name: 'VIEW_SALES_SUMMARY',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.salesSummary.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'smsreports',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-sms-reports',
          },
          permission: {
            name: 'VIEW_REPORTS',
          },
          featureFlag: {
            name: FeatureFlag.SMSReports,
          },
        },
      },
      element: <RouteWrapper element={<SMSReportLanding />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.smsReports.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: 'marketing',
          access: {
            one: {
              featureFlag: {
                name: FeatureFlag.SMSMarketing,
              },
            },
          },
          element: <RouteWrapper element={<Marketing />} />,
        },
        {
          path: 'orderconfirmation',
          access: {
            one: {
              featureFlag: {
                name: FeatureFlag.SMSOrderConfirmation,
              },
            },
          },
          element: <RouteWrapper element={<OrderConfirmation />} />,
        },
        {
          path: 'accountverification',
          access: {
            one: {
              featureFlag: {
                name: FeatureFlag.SMSAccountVerification,
              },
            },
          },
          element: <RouteWrapper element={<AccountVerification />} />,
        },
        {
          path: 'guestcheckoutverification',
          access: {
            one: {
              featureFlag: {
                name: FeatureFlag.SMSGuestCheckoutVerification,
              },
            },
          },
          element: <RouteWrapper element={<GuestCheckoutVerification />} />,
        },
        {
          path: 'passwordlessauthentication',
          access: {
            one: {
              featureFlag: {
                name: FeatureFlag.SMSPasswordlessAuthentication,
              },
            },
          },
          element: <RouteWrapper element={<PasswordlessAuthentication />} />,
        },
      ],
    },
    {
      path: 'loyaltyperformancereport',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-loyalty-performance-report',
          },
          permission: {
            name: 'VIEW_LOYALTY_PERFORMANCE_REPORT',
          },
        },
      },
      element: <RouteWrapper element={<LoyaltyPerformanceReport />} />,
      handle: {
        page: {
          title: () =>
            i18n.t('sidebar.analytics.loyaltyPerformanceReport.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: 'brandloyaltyoverview',
          element: <RouteWrapper element={<BrandLoyaltyOverview />} />,
        },
        {
          path: 'franchiseloyaltyoverview',
          element: <RouteWrapper element={<FranchiseLoyaltyOverview />} />,
        },
        {
          path: 'locationloyaltyperformance',
          element: <RouteWrapper element={<LocationLoyaltyPerformance />} />,
        },
        {
          path: 'locationloyaltytrends',
          element: <RouteWrapper element={<LocationLoyaltyTrends />} />,
        },
        {
          path: 'loyaltyofferperformance',
          element: <RouteWrapper element={<LoyaltyOfferPerformance />} />,
        },
        {
          path: 'loyaltyoffertrends',
          element: <RouteWrapper element={<LoyaltyOfferTrends />} />,
        },
      ],
    },
    {
      path: 'recommendationsreport',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-recommendations',
          },
          permission: {
            name: 'VIEW_RECOMMENDATIONS',
          },
        },
      },
      element: <RouteWrapper element={<RecommendationsReport />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.recommendationsReport.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'email-pulse',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-email-insights',
          },
          permission: {
            name: 'VIEW_EMAIL_INSIGHTS_REPORT',
          },
        },
      },
      element: <RouteWrapper element={<EmailPulse />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.emailPulse.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to="overview" replace />,
        },
        {
          path: 'overview',
          element: <RouteWrapper element={<Overview />} />,
        },
        {
          path: 'messages',
          element: <RouteWrapper element={<EmailPulseMessages />} />,
        },
        {
          path: 'offers',
          element: <RouteWrapper element={<EmailPulseOffers />} />,
        },
        {
          path: 'orders',
          element: <RouteWrapper element={<EmailPulseOrders />} />,
        },
        {
          path: 'gift-cards',
          element: <RouteWrapper element={<EmailPulseGiftCards />} />,
        },
        {
          path: 'security',
          element: <RouteWrapper element={<EmailPulseSecurity />} />,
        },
      ],
    },
    {
      path: 'guest-journey',
      access: {
        one: {
          featureFlag: {
            name: FeatureFlag.GuestJourney,
          },
          statsigFeatureGate: {
            name: 'dashboard-guest-journey',
          },
          permission: {
            name: 'VIEW_GUEST_JOURNEY',
          },
        },
      },
      element: <RouteWrapper element={<GuestJourney />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.analytics.guestJourney.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to="active-customers" replace />,
        },
        {
          path: 'active-customers',
          access: {
            one: {
              statsigFeatureGate: {
                name: GUEST_JOURNEY_TAB_GATES['active-customers'],
              },
            },
          },
          element: <RouteWrapper element={<GuestJourneyActiveCustomers />} />,
        },
        {
          path: 'sliders',
          access: {
            one: {
              statsigFeatureGate: { name: GUEST_JOURNEY_TAB_GATES.sliders },
            },
          },
          element: <RouteWrapper element={<GuestJourneySliders />} />,
        },
        {
          path: 'churned',
          access: {
            one: {
              statsigFeatureGate: { name: GUEST_JOURNEY_TAB_GATES.churned },
            },
          },
          element: <RouteWrapper element={<GuestJourneyChurned />} />,
        },
        {
          path: 'offer-impact',
          access: {
            one: {
              statsigFeatureGate: {
                name: GUEST_JOURNEY_TAB_GATES['offer-impact'],
              },
            },
          },
          element: <RouteWrapper element={<GuestJourneyOfferImpact />} />,
        },
        {
          path: 'retention',
          access: {
            one: {
              statsigFeatureGate: { name: GUEST_JOURNEY_TAB_GATES.retention },
            },
          },
          element: <RouteWrapper element={<GuestJourneyRetention />} />,
        },
        {
          path: 'benchmarks',
          access: {
            one: {
              statsigFeatureGate: { name: GUEST_JOURNEY_TAB_GATES.benchmarks },
            },
          },
          element: <RouteWrapper element={<GuestJourneyBenchmarks />} />,
        },
        {
          path: 'stage-movement',
          access: {
            one: {
              statsigFeatureGate: {
                name: GUEST_JOURNEY_TAB_GATES['stage-movement'],
              },
            },
          },
          element: <RouteWrapper element={<GuestJourneyStageMovement />} />,
        },
        {
          path: 'details',
          access: {
            one: {
              statsigFeatureGate: { name: GUEST_JOURNEY_TAB_GATES.details },
            },
          },
          element: <RouteWrapper element={<GuestJourneyDetails />} />,
        },
      ],
    },
    {
      path: 'guest-journey/settings',
      access: {
        one: {
          featureFlag: {
            name: FeatureFlag.GuestJourney,
          },
          statsigFeatureGate: {
            name: 'dashboard-guest-journey',
          },
          permission: {
            name: 'VIEW_GUEST_JOURNEY',
          },
        },
      },
      element: <RouteWrapper element={<GuestJourneySettings />} />,
      handle: {
        page: {
          title: () => i18n.t('page.guestJourney.settings.title'),
        },
      },
    },
  ],
};

const communicationsRoutes: IncentivioRouteObject = {
  path: 'communications',
  access: {
    any: [
      {
        permission: {
          name: 'VIEW_CAMPAIGN',
        },
      },
      {
        permission: {
          name: 'VIEW_PROMO_CODE',
        },
      },
      {
        permission: {
          name: 'VIEW_OFFER',
        },
      },
      {
        permission: {
          name: 'VIEW_SURVEY',
        },
      },
      {
        permission: {
          name: 'VIEW_LOYALTY_OFFER',
        },
      },
      {
        permission: {
          name: 'VIEW_MESSAGE',
        },
      },
      {
        permission: {
          name: 'VIEW_EMAIL_TEMPLATES',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.communications.title'),
      sidebar: {
        icon: <ChatOutlined />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'campaign',
      element: <RouteWrapper element={<Campaigns />} />,
      access: {
        one: {
          permission: {
            name: 'VIEW_CAMPAIGN',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.campaigns.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'promocode',
      element: <RouteWrapper element={<PromoCodes />} />,
      access: {
        one: {
          permission: {
            name: 'VIEW_PROMO_CODE',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.promocode.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'offers',
      access: {
        one: {
          permission: {
            name: 'VIEW_OFFER',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.offers.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Offers />} />,
        },
        {
          path: 'draft/:draftOfferId',
          access: {
            all: [
              { permission: { name: 'CREATE_OFFER' } },
              { permission: { name: 'EDIT_OFFER' } },
            ],
          },
          element: <RouteWrapper element={<CreateEditOffer />} />,
          handle: {
            page: {
              title: () =>
                i18n.t('page.communications.offers.editDraftOffer.title'),
            },
          },
        },
        {
          path: ':offerId',
          access: {
            one: {
              permission: {
                name: 'EDIT_OFFER',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditOffer />} />,
          handle: {
            page: {
              title: () => i18n.t('page.communications.offers.editOffer.title'),
            },
          },
        },
      ],
    },
    {
      path: 'loyaltyoffers',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-loyalty-offers-page',
          },
          permission: {
            name: 'VIEW_LOYALTY_OFFER',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.loyaltyOffers.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<LoyaltyOffers />} />,
        },
        {
          path: 'draft/:draftOfferId',
          access: {
            all: [
              { permission: { name: 'CREATE_LOYALTY_OFFER' } },
              { permission: { name: 'EDIT_LOYALTY_OFFER' } },
            ],
          },
          element: <RouteWrapper element={<CreateEditOffer />} />,
          handle: {
            page: {
              title: () =>
                i18n.t(
                  'page.communications.offers.editDraftOffer.loyaltyTitle',
                ),
            },
          },
        },
        {
          path: ':offerId',
          access: {
            one: {
              permission: {
                name: 'EDIT_LOYALTY_OFFER',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditOffer />} />,
          handle: {
            page: {
              title: () =>
                i18n.t('page.communications.offers.editOffer.loyaltyTitle'),
            },
          },
        },
      ],
    },
    {
      path: 'messages',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-messages-page',
          },
          permission: {
            name: 'VIEW_MESSAGE',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.messages.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Messages />} />,
        },
        {
          path: 'draft/:draftMessageId',
          access: {
            one: {
              permission: {
                name: 'CREATE_MESSAGE',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditMessage />} />,
          handle: {
            page: {
              title: () =>
                i18n.t('page.communications.messages.editDraftMessage.title'),
            },
          },
        },
        {
          path: ':messageId',
          access: {
            one: {
              permission: {
                name: 'EDIT_MESSAGE',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditMessage />} />,
          handle: {
            page: {
              title: () =>
                i18n.t('page.communications.messages.editMessage.title'),
            },
          },
        },
      ],
    },
    {
      path: 'surveys',
      access: {
        one: {
          permission: {
            name: 'VIEW_SURVEY',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.surveys.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Surveys />} />,
        },
        {
          path: 'create',
          access: {
            one: {
              permission: {
                name: 'CREATE_SURVEY',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditSurvey />} />,
          handle: {
            page: {
              title: () => i18n.t('page.communications.surveys.create.title'),
            },
          },
        },
        {
          path: ':surveyId',
          access: {
            one: {
              permission: {
                name: 'EDIT_SURVEY',
              },
            },
          },
          element: <RouteWrapper element={<CreateEditSurvey />} />,
          handle: {
            page: {
              title: () => i18n.t('page.communications.surveys.edit.title'),
            },
          },
        },
      ],
    },
    {
      path: 'emailtemplate',
      access: {
        one: {
          featureFlag: {
            name: FeatureFlag.SendGrid,
          },
          permission: {
            name: 'VIEW_EMAIL_TEMPLATES',
          },
        },
      },
      element: <RouteWrapper element={<EmailTemplates />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.communications.emailTemplate.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to="offer" replace />,
        },
        {
          path: 'offer',
          element: <RouteWrapper element={<OfferEmails />} />,
        },
        {
          path: 'loyaltyreward',
          element: <RouteWrapper element={<LoyaltyRewardEmails />} />,
        },
        {
          path: 'message',
          element: <RouteWrapper element={<MessageEmails />} />,
        },
        {
          access: {
            one: {
              statsigFeatureGate: {
                name: 'email-templates-events',
              },
            },
          },
          path: 'event',
          element: <RouteWrapper element={<EventEmails />} />,
        },
      ],
    },
  ],
};

const paymentsRoutes: IncentivioRouteObject = {
  path: 'payments',
  access: {
    one: {
      featureFlag: {
        name: FeatureFlag.IncPayments,
      },
    },
    any: [
      {
        permission: {
          name: 'VIEW_REPORTS',
        },
      },
      {
        permission: {
          name: 'CREATE_ACCOUNTS',
        },
      },
      {
        permission: {
          name: 'VIEW_TRANSACTIONS',
        },
      },
      {
        permission: {
          name: 'VIEW_ACCOUNTS',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.payments.title'),
      sidebar: {
        icon: <CreditCardIcon />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'accounts',
      access: {
        any: [
          {
            permission: {
              name: 'VIEW_ACCOUNTS',
            },
          },
          {
            permission: {
              name: 'CREATE_ACCOUNTS',
            },
          },
        ],
      },
      element: <RouteWrapper element={<Accounts />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.payments.accounts.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'transactions',
      access: {
        one: {
          permission: {
            name: 'VIEW_TRANSACTIONS',
          },
        },
      },
      element: <RouteWrapper element={<Transactions />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.payments.transactions.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
    },
    {
      path: 'reports',
      access: {
        one: {
          permission: {
            name: 'VIEW_REPORTS',
          },
        },
      },
      element: <RouteWrapper element={<ReportsLanding />} />,
      handle: {
        page: {
          title: () => i18n.t('sidebar.payments.reports.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <Navigate to="transactions" replace />,
        },
        {
          path: 'transactions',
          element: <RouteWrapper element={<Reports />} />,
        },
        {
          path: 'deposits',
          element: <RouteWrapper element={<DepositsSummary />} />,
        },
      ],
    },
    {
      path: 'chargebacks',
      handle: {
        page: {
          title: () => i18n.t('sidebar.payments.chargebacks.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Chargebacks />} />,
        },
        {
          path: ':disputeId',
          element: <RouteWrapper element={<DisputeChargebackForm />} />,
        },
      ],
    },
  ],
};

const crmRoutes: IncentivioRouteObject = {
  path: 'crm',
  access: {
    any: [
      {
        permission: {
          name: 'VIEW_CUSTOMER',
        },
      },
      {
        permission: {
          name: 'VIEW_EXTENSIBLE_ATTRIBUTES',
        },
      },
      {
        permission: {
          name: 'VIEW_SEGMENT',
        },
        statsigFeatureGate: {
          name: 'dashboard-segments',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.crm.title'),
      sidebar: {
        icon: <PeopleAltOutlined />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'customers',
      access: {
        one: {
          permission: {
            name: 'VIEW_CUSTOMER',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.crm.customers.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Customers />} />,
        },
        {
          path: ':customerId',
          element: <RouteWrapper element={<CustomerDetails />} />,
          handle: {
            page: {
              title: () => i18n.t('page.customers.title'),
            },
          },
        },
      ],
    },
    {
      path: 'segments',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-segments',
          },
          permission: {
            name: 'VIEW_SEGMENT',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.crm.segments.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Segments />} />,
        },
        {
          path: 'create',
          element: <RouteWrapper element={<CreateSegment />} />,
          access: {
            one: {
              permission: {
                name: 'CREATE_SEGMENT',
              },
            },
          },
          handle: {
            page: {
              title: () => i18n.t('page.segment.create.title'),
            },
          },
        },
        {
          path: 'edit/:segmentId',
          access: {
            one: {
              permission: {
                name: 'UPDATE_SEGMENT',
              },
            },
          },
          element: <RouteWrapper element={<CreateSegment />} />,
          handle: {
            page: {
              title: () => i18n.t('page.segment.edit.title'),
            },
          },
        },
      ],
    },
    {
      path: 'customertags',
      access: {
        one: {
          permission: {
            name: 'VIEW_EXTENSIBLE_ATTRIBUTES',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.crm.customerTags.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      element: <RouteWrapper element={<CustomerTags />} />,
    },
  ],
};

const menuManagementRoutes: IncentivioRouteObject = {
  path: 'menus',
  access: {
    any: [
      {
        permission: {
          name: 'VIEW_BULK_IMAGE_UPLOADS',
        },
      },
      {
        permission: {
          name: 'VIEW_ITEM',
        },
      },
      {
        permission: {
          name: 'VIEW_MENU_IMPORT_HISTORY',
        },
      },
    ],
  },
  handle: {
    page: {
      title: () => i18n.t('sidebar.menus.title'),
      sidebar: {
        icon: <RestaurantMenuOutlined />,
        component: SidebarSection,
      },
    },
  },
  children: [
    {
      path: 'imageupload',
      access: {
        one: {
          permission: {
            name: 'VIEW_BULK_IMAGE_UPLOADS',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.images.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<ImageUpload />} />,
        },
        {
          path: ':uploadId',
          element: <RouteWrapper element={<ImageUploadDetails />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.imageUpload.details.title'),
            },
          },
        },
      ],
    },
    {
      path: 'sync',
      access: {
        one: {
          permission: {
            name: 'VIEW_MENU_IMPORT_HISTORY',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.menuSync.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<MenuSync />} />,
        },
      ],
    },
    {
      path: 'menus',
      access: {
        one: {
          permission: {
            name: 'VIEW_MENU',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.menus.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: '',
          element: <RouteWrapper element={<MenusLanding />} />,
          children: [
            {
              index: true,
              element: <Navigate to="imported" replace />,
            },
            {
              path: 'imported',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<MenusImported />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.menus.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'native',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<MenusNative />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.menus.title'),
                  breadcrumb: false,
                },
              },
            },
          ],
        },
        {
          path: 'create',
          access: {
            one: {
              permission: {
                name: 'CREATE_MENU',
              },
            },
          },
          element: <RouteWrapper element={<MenusCreate />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.menus.create.title'),
            },
          },
        },
        {
          path: ':catalogId',
          access: {
            one: {
              permission: {
                name: 'EDIT_MENU',
              },
            },
          },
          element: <RouteWrapper element={<MenuEdit />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.menus.edit.title'),
            },
          },
        },
      ],
    },
    {
      path: 'groups',
      access: {
        one: {
          permission: {
            name: 'VIEW_GROUP',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.groups.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: '',
          element: <RouteWrapper element={<GroupsLanding />} />,
          children: [
            {
              index: true,
              element: <Navigate to="imported" replace />,
            },
            {
              path: 'imported',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<GroupsImported />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.groups.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'native',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<GroupsNative />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.groups.title'),
                  breadcrumb: false,
                },
              },
            },
          ],
        },
        {
          path: 'create',
          access: {
            one: {
              permission: {
                name: 'CREATE_GROUP',
              },
            },
          },
          element: <RouteWrapper element={<CreateGroup />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.groups.create.title'),
            },
          },
        },
        {
          path: ':groupId',
          access: {
            one: {
              permission: {
                name: 'EDIT_GROUP',
              },
            },
          },
          element: <RouteWrapper element={<EditGroup />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.groups.edit.title'),
            },
          },
        },
      ],
    },
    {
      path: 'items',
      access: {
        one: {
          permission: {
            name: 'VIEW_ITEM',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.items.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: '',
          element: <RouteWrapper element={<ItemsLanding />} />,
          children: [
            {
              index: true,
              element: <Navigate to="imported" replace />,
            },
            {
              path: 'imported',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<ItemsImported />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.items.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'native',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<ItemsNative />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.items.title'),
                  breadcrumb: false,
                },
              },
            },
          ],
        },
        {
          path: 'create',
          access: {
            one: {
              permission: {
                name: 'CREATE_ITEM',
              },
            },
          },
          element: <RouteWrapper element={<CreateItem />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.items.create.title'),
            },
          },
        },
        {
          path: ':itemId',
          access: {
            one: {
              permission: {
                name: 'EDIT_ITEM',
              },
            },
          },
          element: <RouteWrapper element={<EditItem />} />,
          handle: {
            page: {
              title: () => i18n.t('page.menus.items.edit.title'),
            },
          },
        },
      ],
    },
    {
      path: 'recommendations',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-recommendations',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('sidebar.menus.recommendations.title'),
          sidebar: {
            component: SidebarLink,
          },
        },
      },
      children: [
        {
          path: '',
          element: <RouteWrapper element={<Recommendations />} />,
          children: [
            {
              index: true,
              element: <Navigate to="itemanalysis" replace />,
            },
            {
              path: 'itemanalysis',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<ItemAnalysis />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.recommendations.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'customrules',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<CustomRules />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.recommendations.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'itemclassifier',
              children: [
                {
                  index: true,
                  element: <RouteWrapper element={<ItemClassifier />} />,
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.recommendations.title'),
                  breadcrumb: false,
                },
              },
            },
            {
              path: 'settings',
              access: {
                one: {
                  permission: {
                    name: 'VIEW_RECOMMENDATIONS_SETTINGS',
                  },
                },
              },
              children: [
                {
                  index: true,
                  element: (
                    <RouteWrapper element={<RecommendationsSettings />} />
                  ),
                },
              ],
              handle: {
                page: {
                  title: () => i18n.t('sidebar.menus.recommendations.title'),
                  breadcrumb: false,
                },
              },
            },
          ],
        },
      ],
    },
  ],
};

const orderDetailsRoutes: IncentivioRouteObject = {
  path: 'order',
  access: {
    any: [
      {
        permission: {
          name: 'VIEW_DAILY_ORDERS_REPORT',
        },
      },
    ],
  },
  children: [
    {
      path: ':orderId',
      element: <RouteWrapper element={<OrderDetails />} />,
    },
  ],
  handle: {
    page: {
      title: () => i18n.t('page.salesreport.orderDetails.title'),
    },
  },
};

const menuRoutes: IncentivioRouteObject = {
  handle: {
    page: {
      title: () => null,
      sidebar: {
        component: SidebarGroup,
      },
    },
  },
  children: [
    locationRoutes,
    onlineOrdersRoutes,
    analyticsRoutes,
    crmRoutes,
    menuManagementRoutes,
    communicationsRoutes,
    paymentsRoutes,
    orderDetailsRoutes,
    giftCardsRoutes,
    appearanceRoutes,
  ],
};

const settingsRoutes: IncentivioRouteObject = {
  path: 'settings',
  handle: {
    page: {
      title: () => i18n.t('sidebar.settings.title'),
      sidebar: {
        icon: <SettingsOutlined fontSize="small" />,
        component: SidebarBottomLink,
      },
    },
  },
  children: [
    {
      index: true,
      element: <RouteWrapper element={<Settings />} />,
    },
    {
      path: 'loyalty',
      access: {
        one: {
          permission: {
            name: 'EDIT_MERCHANT',
          },
        },
      },
      element: <RouteWrapper element={<LoyaltySpec />} />,
      handle: {
        page: {
          title: () => i18n.t('page.settings.loyalty-specs.title'),
        },
      },
    },
    {
      path: 'employees',
      access: {
        one: {
          permission: {
            name: 'VIEW_USER',
          },
        },
      },
      element: <RouteWrapper element={<Employees />} />,
      handle: {
        page: {
          title: () => i18n.t('page.employees.title'),
        },
      },
    },
    {
      path: 'roles',
      access: {
        one: {
          permission: {
            name: 'VIEW_ROLE',
          },
        },
      },
      element: <RouteWrapper element={<Roles />} />,
      handle: {
        page: {
          title: () => i18n.t('page.roles.title'),
        },
      },
    },
    {
      path: 'client',
      access: {
        one: {
          permission: {
            name: 'CONFIGURE_CLIENT',
          },
        },
      },
      element: <RouteWrapper element={<Client />} />,
      handle: {
        page: {
          title: () => i18n.t('page.client.title'),
        },
      },
    },
    {
      path: 'clientconfiguration',
      access: {
        one: {
          permission: {
            name: 'CONFIGURE_CLIENT',
          },
        },
      },
      element: <RouteWrapper element={<ClientConfiguration />} />,
      handle: {
        page: {
          title: () => i18n.t('page.clientConfiguration.title'),
        },
      },
    },
    {
      path: 'integrations',
      access: {
        one: {
          statsigFeatureGate: {
            name: 'dashboard-integrations',
          },
        },
      },
      handle: {
        page: {
          title: () => i18n.t('page.integrations.title'),
        },
      },
      children: [
        {
          index: true,
          element: <RouteWrapper element={<Integrations />} />,
        },
        {
          path: 'giftcard/:giftCardProvider',
          element: <RouteWrapper element={<GiftCardProviderConfiguration />} />,
          handle: {
            page: {
              title: () => i18n.t('page.integrations.tabs.giftCards.label'),
            },
          },
        },
      ],
    },
    {
      path: 'general',
      access: {
        one: {
          permission: {
            name: 'EDIT_MERCHANT',
          },
        },
      },
      element: <RouteWrapper element={<General />} />,
      handle: {
        page: {
          title: () => i18n.t('page.settings.cards.general.title'),
        },
      },
    },
  ],
};

const otherRoutes: IncentivioRouteObject = {
  handle: {
    page: {
      sidebar: {
        component: SidebarBottomGroup,
      },
    },
  },
  children: [settingsRoutes],
};

/** This is not defined directly in app-routes in order to avoid circular dependency issues */
const mainRoutes: IncentivioRouteObject[] = [
  {
    path: '*',
    element: <NotFound />,
  },
  {
    path: '/',
    children: [
      {
        index: true,
        element: <RouteWrapper element={<Welcome />} />,
      },
      menuRoutes,
      otherRoutes,
    ],
  },
];

export default mainRoutes;
